import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import NotificationController from './notification_controller'


// Base controller for all upload functionality
// Provides common methods for DirectUpload, progress tracking, and form handling
export default class extends Controller {
  
  connect() {
    this.uploadQueue = []
    this.completedUploads = []
    this.setupProgressModal()
  }
  
  // Core DirectUpload functionality - DRY implementation
  async performDirectUpload(file, progressCallback) {
    const url = '/rails/active_storage/direct_uploads'
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    
    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXMLHttpRequest: (request) => {
        // Important: Include credentials for production with SSL
        request.withCredentials = true
        request.setRequestHeader('X-CSRF-Token', token)
        
        request.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            progressCallback?.(progress)
          }
        })
      }
    })
    
    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob)
        }
      })
    })
  }
  
  // Progress modal management - shared across all uploaders
  setupProgressModal() {
    // Find or create the upload progress modal container
    let modalContainer = document.getElementById('upload-progress-modal-container')
    if (!modalContainer) {
      modalContainer = document.createElement('div')
      modalContainer.id = 'upload-progress-modal-container'
      document.body.appendChild(modalContainer)
    }
  }
  
  getProgressModalController() {
    const modalElement = document.getElementById('upload-progress-modal')
    if (modalElement) {
      const application = this.application
      return application.getControllerForElementAndIdentifier(modalElement, 'upload-progress-modal')
    }
    return null
  }
  
  // Async version that waits for modal to be available
  async getProgressModalControllerAsync() {
    return new Promise((resolve) => {
      let modalElement = document.getElementById('upload-progress-modal')
      
      if (modalElement) {
        const controller = this.application.getControllerForElementAndIdentifier(modalElement, 'upload-progress-modal')
        resolve(controller)
        return
      }
      
      // Wait for modal to appear
      const checkInterval = setInterval(() => {
        modalElement = document.getElementById('upload-progress-modal')
        if (modalElement) {
          clearInterval(checkInterval)
          const controller = this.application.getControllerForElementAndIdentifier(modalElement, 'upload-progress-modal')
          resolve(controller)
        }
      }, 100)
      
      // Timeout after 2 seconds
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve(null)
      }, 2000)
    })
  }
  
  // Form submit button management - DRY implementation
  disableSubmit() {
    const submitButtons = this.element.querySelectorAll('[type="submit"]')
    submitButtons.forEach(button => {
      button.disabled = true
      button.dataset.originalText = button.textContent
      button.textContent = 'Upload en cours...'
    })
  }
  
  enableSubmit() {
    const submitButtons = this.element.querySelectorAll('[type="submit"]')
    submitButtons.forEach(button => {
      button.disabled = false
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText
      }
    })
  }
  
  // Form interceptor setup - common pattern
  setupFormInterceptor(uploadHandler) {
    const form = this.element.querySelector('form') || this.element
    if (!form || form.tagName !== 'FORM') {
      console.error('No form found for upload controller')
      return
    }
    
    
    form.addEventListener('submit', async (event) => {
      // Find all file inputs with files
      const fileInputs = form.querySelectorAll('input[type="file"]')
      let hasFiles = false
      
      fileInputs.forEach(input => {
        if (input.files && input.files.length > 0) {
          hasFiles = true
        }
      })
      
      if (!hasFiles) {
        return // Allow normal submission
      }
      
      // Prevent default submission
      event.preventDefault()
      
      // Disable submit button
      this.disableSubmit()
      
      try {
        // Call the specific upload handler
        await uploadHandler(form, fileInputs)
      } catch (error) {
        NotificationController.error('Erreur d\'upload', error.message)
        this.enableSubmit()
      }
    })
  }
  
  // Helper to extract stem index from input name
  extractStemIndex(input) {
    const match = input.name.match(/\[stems_attributes\]\[(\d+|NEW_NESTED_STEM)\]/)
    return match ? match[1] : 'unknown'
  }
  
  // Create hidden input for signed blob ID
  replaceFileInputWithSignedId(fileInput, signedId) {
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = fileInput.name.replace('[audio_file]', '[signed_blob_id]')
    hiddenInput.value = signedId
    
    
    fileInput.parentNode.insertBefore(hiddenInput, fileInput)
    fileInput.value = '' // Clear the file
    fileInput.style.display = 'none' // Hide the file input
  }
  
  // Helper to get CSRF token
  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
  
  // Common cleanup on disconnect
  disconnect() {
    // Cleanup any intervals or listeners if needed
  }
}